import React, { useState } from "react";
import LabelInput from "../common/forms/Labelinput";
import { TrendingUp } from "lucide-react";
import SelectMenu from "../common/forms/selectmenu";
import Button from "../common/ui/button";
import CancelButton from "../common/ui/cancelbutton";
import DateLabelInput from "../common/forms/DateLabelInput";
import NoFound from "../common/ui/NoFound";
import { MdEditSquare } from "react-icons/md";
import { RiDeleteBin5Line } from "react-icons/ri";
import WarningModal from "../common/ui/warningmodel";

export default function AddInBondEntryForm({
  biNumberList,
  loadingBINumber,
  materialList,
  loadingMaterials,
  formData,
  updateFormData,
  records,
  setRecords,
}) {

  const [editIndex, setEditIndex] = useState(null);
  const [warningVisible, setWarningVisible] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  const handleAddEntry = () => {
    const { biNumber, beNumber, beDate, inbondDate, material, weight, invoiceValue, dollar, price, duty, assessment } = formData;

    if (!biNumber || !beNumber || !material || !weight || !invoiceValue || !duty || !assessment) {
      setWarningMessage("Please fill all required fields!");
      setWarningVisible(true);
      return;
    }

    if (inbondDate && beDate && new Date(inbondDate) < new Date(beDate)) {
      setWarningMessage("In-bond Date cannot be before BE Date.");
      setWarningVisible(true);
      return;
    }

    const totalAmount = Number(invoiceValue || 0) * Number(weight || 0);

    const newRecord = { biNumber, beNumber, beDate, inbondDate, material, weight, invoiceValue, dollar, price, duty, assessment, totalAmount };

    if (editIndex !== null) {
      // Update existing record
      const updatedRecords = [...records];
      updatedRecords[editIndex] = newRecord;
      setRecords(updatedRecords);
      setEditIndex(null); // reset edit state
    } else {
      // Add new record
      setRecords([...records, newRecord]);
    }

    // Reset only material/financial fields
    ["material", "weight", "invoiceValue", "dollar", "price", "duty", "assessment"].forEach((field) =>
      updateFormData(field, "")
    );
  };

  const handleEditEntry = (index) => {
    const entry = records[index];
    Object.keys(entry).forEach((key) => updateFormData(key, entry[key]));
    setEditIndex(index); // mark which record is being edited
  };


  // Add this function inside your component
  const handleDeleteEntry = (index) => {
    const updatedRecords = [...records];
    updatedRecords.splice(index, 1); // remove the clicked entry
    setRecords(updatedRecords);
  };

  const resetForm = () => {
    const fields = [
      "material",
      "weight",
      "invoiceValue",
      "dollar",
      "price",
      "duty",
      "assessment",
    ];
    fields.forEach((field) => updateFormData(field, ""));
    setEditIndex(null); // clear edit mode
  };


  // === Date normalization logic ===
  const normalizeDate = (dateInput) => {
    if (!dateInput) return "";
    if (dateInput instanceof Date) {
      const yyyy = dateInput.getFullYear();
      const mm = String(dateInput.getMonth() + 1).padStart(2, "0");
      const dd = String(dateInput.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
    const str = String(dateInput).replace(/\//g, "-");
    const parts = str.split("-");
    if (parts.length === 3 && parts[0].length === 2) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return str;
  };

  const compareDates = (d1, d2) => {
    if (!d1 || !d2) return 0;
    const date1 = new Date(`${normalizeDate(d1)}T00:00:00`);
    const date2 = new Date(`${normalizeDate(d2)}T00:00:00`);
    if (date1.getTime() < date2.getTime()) return -1;
    if (date1.getTime() > date2.getTime()) return 1;
    return 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    return `${day} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatIndianNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return "0";
    return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleBeDateChange = (e) => {
    const value = e?.target?.value;
    const beDate = normalizeDate(value);
    updateFormData("beDate", beDate);
  };

  const handleInbondDateChange = (e) => {
    const value = e?.target?.value;
    const inbondDate = normalizeDate(value);
    if (formData.beDate && inbondDate) {
      const comparison = compareDates(inbondDate, formData.beDate);
      if (comparison < 0) {
        setWarningMessage("In-bond Date cannot be before BE Date.");
        setWarningVisible(true);
        return;
      }
    }
    updateFormData("inbondDate", inbondDate);
  };

  const totalDuty = records.reduce((acc, r) => acc + Number(r.duty || 0), 0);
  const totalWeight = records.reduce((acc, r) => acc + Number(r.weight || 0), 0);
  const totalAssessment = records.reduce((acc, r) => acc + Number(r.assessment || 0), 0);
  const totalAmount = records.reduce((acc, r) => acc + Number(r.totalAmount || 0), 0);

  // === Determine if Add Entry button should be enabled ===
  const canAddEntry = (() => {
    const { biNumber, beNumber, beDate, inbondDate, material, weight, invoiceValue, duty, assessment } = formData;
    if (!biNumber || !beNumber || !beDate || !inbondDate || !material || !weight || !invoiceValue || !duty || !assessment) {
      return false;
    }
    if (compareDates(inbondDate, beDate) < 0) return false;
    return true;
  })();

  return (
    <>
      <div className="space-y-4 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-6 items-start">
          {/* LEFT SIDE FORM */}
          <div className="lg:col-span-4 col-span-12 bg-white shadow-md border border-gray-100 rounded p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <LabelInput
                label="BI No."
                type="text"
                value={`BI ${formData.biNumber || ""}`}
                onChange={(e) => {
                  const val = e.target.value.replace(/^BI\s?/, "").trim();
                  if (biNumberList.some((item) => item.label.toLowerCase() === `BI ${val}`.toLowerCase())) {
                    setWarningMessage("This BI number already exists!");
                    setWarningVisible(true);
                    return;
                  }
                  updateFormData("biNumber", val);
                }}
                loading={loadingBINumber}
                required
              />
              <LabelInput
                label="BE No."
                type="text"
                value={formData.beNumber || ""}
                onChange={(e) => updateFormData("beNumber", e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <DateLabelInput
                label="BE Date"
                type="date"
                value={formData.beDate || ""}
                onChange={handleBeDateChange}
                required
              />
              <DateLabelInput
                label="In-bond Date"
                type="date"
                value={formData.inbondDate || ""}
                onChange={handleInbondDateChange}
                required
                min={formData.beDate || ""} // restrict selection in date picker
              />
            </div>

            <div className="py-1.5 border-t border-dashed border-gray-300 w-full"></div>

            <h2 className="text-sm sm:text-base font-semibold text-gray-800 flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              Material & Financial Details
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <SelectMenu
                label="Material"
                options={materialList}
                value={formData.material || ""}
                onChange={(val) => updateFormData("material", val)}
                searchable
                required
                loading={loadingMaterials}
                usePortal
              />
              <LabelInput
                label="Weight (kg)"
                type="number"
                value={formData.weight || ""}
                onChange={(e) => updateFormData("weight", e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <LabelInput
                label="Invoice Value ($)"
                type="number"
                value={formData.invoiceValue || ""}
                onChange={(e) => updateFormData("invoiceValue", e.target.value)}
                required
              />
              <LabelInput
                label="Dollar (₹) (ER)"
                type="number"
                value={formData.dollar || ""}
                onChange={(e) => updateFormData("dollar", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <LabelInput
                label="Price"
                type="number"
                value={formData.price || ""}
                onChange={(e) => updateFormData("price", e.target.value)}
              />
              <LabelInput
                label="Duty In-bond (₹)"
                type="number"
                value={formData.duty || ""}
                onChange={(e) => updateFormData("duty", e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <LabelInput
                label="Assessment Value (₹)"
                type="number"
                value={formData.assessment || ""}
                onChange={(e) => updateFormData("assessment", e.target.value)}
                required
              />
              <LabelInput
                label="Total Amount material ($)"
                type="text"
                value={
                  formData.weight && formData.invoiceValue
                    ? (Number(formData.weight) * Number(formData.invoiceValue)).toFixed(2)
                    : ""
                }
                readOnly
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                className={`w-full h-8 text-xs font-medium px-3 ${!canAddEntry ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={handleAddEntry}
                disabled={!canAddEntry}
              >
                Add Entry
              </Button>
              <CancelButton
                onClick={resetForm}
                type="button"
                className="w-full sm:w-auto">
                Reset
              </CancelButton>
            </div>
          </div>

          {/* RIGHT SIDE TABLE */}
          <div className="lg:col-span-8 col-span-12 space-y-4">
            {records.length > 0 ? (
              <>
                {/* Summary Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full text-[12px] sm:text-[13px] text-gray-700">
                    <tbody>
                      {[
                        ["BI Number", `BI ${formData.biNumber}`, "BE Number", formData.beNumber],
                        ["BE Date", formatDate(formData.beDate), "In-bond Date", formatDate(formData.inbondDate)],
                        ["Total Duty In-bond", formatIndianNumber(totalDuty.toFixed(2)), "Total Weight", formatIndianNumber(totalWeight.toFixed(2))],
                        ["Total Assessment Value", formatIndianNumber(totalAssessment.toFixed(2)), "Total Amount of Material", formatIndianNumber(totalAmount.toFixed(2))],
                      ].map((row, i) => (
                        <tr key={i} className="border-b border-gray-200 bg-white">
                          <td className="px-2 py-2 text-gray-600 font-medium w-[25%]">{row[0]}</td>
                          <td className="px-2 py-2 font-semibold text-gray-900 w-[25%]">{row[1]}</td>
                          <td className="px-2 py-2 text-gray-600 font-medium w-[25%]">{row[2]}</td>
                          <td className="px-2 py-2 font-semibold text-gray-900 w-[25%]">{row[3]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Material Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-[700px] sm:min-w-full text-[11px] sm:text-[12px] text-center">
                    <thead className="h-10 border-b border-gray-200 text-gray-600 font-medium">
                      <tr>
                        <th>Material</th>
                        <th>Weight</th>
                        <th>Invoice Value</th>
                        <th>Dollar</th>
                        <th>Price</th>
                        <th>Duty In-bond</th>
                        <th>Assessment Value</th>
                        <th>Total Amount</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((entry, i) => (
                        <tr key={i} className="border-t h-10 border-gray-100 hover:bg-gray-50">
                          <td>
                            {materialList.find((m) => m.value === entry.material)?.label || ""}
                          </td>
                          <td>{formatIndianNumber(Number(entry.weight || 0).toFixed(2))}</td>
                          <td>{formatIndianNumber(Number(entry.invoiceValue || 0).toFixed(2))}</td>
                          <td>{formatIndianNumber(Number(entry.dollar || 0).toFixed(2))}</td>
                          <td>{formatIndianNumber(Number(entry.price || 0).toFixed(2))}</td>
                          <td>{formatIndianNumber(Number(entry.duty || 0).toFixed(2))}</td>
                          <td>{formatIndianNumber(Number(entry.assessment || 0).toFixed(2))}</td>
                          <td>{formatIndianNumber(Number(entry.totalAmount || 0).toFixed(2))}</td>
                          <td>
                            <div className="flex items-center justify-center space-x-1">
                              <button
                                onClick={() => handleEditEntry(i)}
                                className="text-green-500 hover:text-green-700 p-1" title="Edit Entry">
                                <MdEditSquare size={16} />
                              </button>
                              <button
                                onClick={() => handleDeleteEntry(i)}
                                className="text-red-500 hover:text-red-700 p-1" title="Delete Entry">
                                <RiDeleteBin5Line size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <NoFound />
            )}
          </div>
        </div>
      </div>
      {warningVisible && (
        <WarningModal
          message={warningMessage}
          onClose={() => setWarningVisible(false)}
        />
      )}

    </>
  );
}
